/**
 * The public Beatly library (/library) — the link every brief gives creators when the brand has not picked a track:
 * search, filters, featured collections, play and download, no login. Only public (auth:false) catalogue calls run
 * here: the library is shown in `publicMode` (no "Add to beat", no AttachToCampaignDialog). `?playlist=<id>` opens a
 * collection. Each title links to the track's own public page (/sound/:id).
 */
import { useEffect, useMemo } from 'react';
import beatlyLogotype from '@/cowork/mobileReview/beatly-logotype-white.svg';
import { AudioPlayerProvider, MiniPlayer, usePlayerInset } from './AudioPlayer';
import { LibrarySection } from './LibrarySection';
import { PUBLIC_SOUND_LICENSE } from './rights';

function PublicLibraryBody() {
  const playlistId = useMemo(() => new URLSearchParams(window.location.search).get('playlist'), []);
  // Keep the last rows clear of the docked player.
  const inset = usePlayerInset();
  return (
    <div className="flex min-h-screen flex-col bg-[#f8f9fc]">
      <header className="flex items-center gap-3 bg-gradient-to-r from-[#1b0730] to-[#111318] px-5 py-3">
        <a href="https://beatly.com" target="_blank" rel="noreferrer" aria-label="Beatly">
          <img src={beatlyLogotype} alt="Beatly" className="h-5 w-auto" />
        </a>
        <span className="min-w-0 flex-1" />
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">Beatly library</span>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6" style={{ paddingBottom: inset + 24 }}>
        <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>Beatly library</h1>
        {/* One line of rights up front; the full terms open on demand (same copy as a sound's public page). */}
        <div className="mt-1 mb-6 text-[12.5px] leading-[18px] text-[#475467]">
          <p>{PUBLIC_SOUND_LICENSE.summary}</p>
          <details className="mt-1">
            <summary className="cursor-pointer list-none text-[#667085] underline-offset-2 hover:text-[#344054] hover:underline [&::-webkit-details-marker]:hidden">
              {PUBLIC_SOUND_LICENSE.title}
            </summary>
            <ul className="mt-2 space-y-1.5">
              {PUBLIC_SOUND_LICENSE.items.map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#be43ff]" />
                  {item}
                </li>
              ))}
            </ul>
          </details>
        </div>
        <LibrarySection publicMode initialPlaylistId={playlistId} />
      </main>
      {/* No app sidebar here, so the player docks full width (the inline bar, pinned to the bottom). */}
      <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3">
        <div className="mx-auto max-w-6xl"><MiniPlayer variant="inline" /></div>
      </div>
    </div>
  );
}

export function PublicLibraryPage() {
  useEffect(() => { document.title = 'Beatly library'; }, []);
  return (
    <AudioPlayerProvider>
      <PublicLibraryBody />
    </AudioPlayerProvider>
  );
}
